import React from 'react';
import './NavSidebar.css';
import { IconMonteCarlo, IconValidate, IconGate, IconRadar, IconEtf } from './Icons';

interface Props {
  currentPage: string;
  onNavigate: (page: any) => void;
}

/* ── Home grid icon ─────────────────────────────────────────────── */
function IconHome() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <defs>
        <linearGradient id="hg1" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="1"/>
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.50"/>
        </linearGradient>
        <linearGradient id="hg2" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.48"/>
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.20"/>
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="9" height="9" rx="2" fill="url(#hg1)"/>
      <rect x="13" y="2" width="9" height="9" rx="2" fill="url(#hg2)"/>
      <rect x="2" y="13" width="9" height="9" rx="2" fill="url(#hg2)"/>
      <rect x="13" y="13" width="9" height="9" rx="2" fill="url(#hg1)"/>
      <rect x="3.5" y="3.5" width="3.5" height="1" rx="0.5" fill="#fff" opacity="0.20"/>
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════════
   METALLIC GRADIENT HELPERS — één consistente lichtbron (linksboven)
   Koppen: radialGradient (bolvormig licht)
   Lichamen: linearGradient (vlak, directioneel)
   ══════════════════════════════════════════════════════════════════ */

/* ── PION — Staunton pawn: bolle kop, slanke taille, brede rok ── */
function SvgPion() {
  return (
    <svg width="24" height="33" viewBox="0 0 28 37" fill="none" aria-hidden>
      <defs>
        {/* Bol-kop: licht linksboven, donker rechtsboven */}
        <radialGradient id="pn-hd" cx="36%" cy="28%" r="72%">
          <stop offset="0%"   stopColor="#e8e8e8"/>
          <stop offset="38%"  stopColor="#b2b2b2"/>
          <stop offset="75%"  stopColor="#606060"/>
          <stop offset="100%" stopColor="#1a1a1a"/>
        </radialGradient>
        {/* Lichaam: diagonaal licht (links) naar donker (rechts-onder) */}
        <linearGradient id="pn-bd" x1="0" y1="0" x2="1" y2="1.2">
          <stop offset="0%"   stopColor="#d2d2d2"/>
          <stop offset="35%"  stopColor="#a0a0a0"/>
          <stop offset="72%"  stopColor="#585858"/>
          <stop offset="100%" stopColor="#1c1c1c"/>
        </linearGradient>
        {/* Basis: iets zwaarder donker voor gronding */}
        <linearGradient id="pn-bs" x1="0" y1="0" x2="0.8" y2="1">
          <stop offset="0%"   stopColor="#c0c0c0"/>
          <stop offset="45%"  stopColor="#808080"/>
          <stop offset="100%" stopColor="#141414"/>
        </linearGradient>
        {/* Rand-schaduw onder de kop */}
        <radialGradient id="pn-sh" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#000" stopOpacity="0.38"/>
          <stop offset="100%" stopColor="#000" stopOpacity="0"/>
        </radialGradient>
      </defs>

      {/* Gronding-schaduw kop (geeft 3D-diepte) */}
      <ellipse cx="14" cy="17.2" rx="5.5" ry="1.8" fill="url(#pn-sh)"/>

      {/* === KOP === */}
      <circle cx="14" cy="9.5" r="7" fill="url(#pn-hd)"/>
      {/* Specular highlight — wit stipje linksboven */}
      <ellipse cx="11.0" cy="7.0" rx="2.5" ry="1.8" fill="#fff" fillOpacity="0.42"/>
      {/* Subtiele second highlight */}
      <ellipse cx="13.2" cy="5.8" rx="0.9" ry="0.7" fill="#fff" fillOpacity="0.22"/>

      {/* === KRAAG/ROSCETTE (smal tussenring) === */}
      <ellipse cx="14" cy="16.8" rx="4.8" ry="2.0" fill="#000" fillOpacity="0.30"/>
      <ellipse cx="14" cy="16.5" rx="4.2" ry="1.7" fill="url(#pn-bd)"/>
      <ellipse cx="14" cy="15.5" rx="3.5" ry="1.3" fill="url(#pn-hd)"/>

      {/* === LICHAAM (S-curve Staunton silhouet) === */}
      {/* Links: van kraag naar basis, gebogen */}
      <path
        d="M 10.5,15.8
           C 9.0,17.0  6.5,20.5  5.5,27.0
           L 22.5,27.0
           C 21.5,20.5  19.0,17.0  17.5,15.8
           Z"
        fill="url(#pn-bd)"
      />
      {/* Links-licht strip op het lichaam */}
      <path
        d="M 10.5,15.8
           C 9.8,17.0  9.0,19.5  8.5,22.8
           L 10.2,22.8
           C 10.8,19.8  11.4,17.5  11.8,16.5
           Z"
        fill="#fff" fillOpacity="0.10"
      />

      {/* === BASIS PLATFORM === */}
      {/* Top-ellips basis (overgangsvlak) */}
      <ellipse cx="14" cy="27.0" rx="9.5" ry="2.4" fill="url(#pn-bd)"/>
      {/* Basis rect (afgevlakt) */}
      <rect x="4" y="27.8" width="20" height="4.8" rx="2.4" fill="url(#pn-bs)"/>
      {/* Basis highlight (licht reflectie linksboven) */}
      <ellipse cx="11.5" cy="28.5" rx="6.5" ry="1.1" fill="#fff" fillOpacity="0.13"/>
      {/* Basis bodem (grondlijn) */}
      <rect x="3.5" y="31.8" width="21" height="1.8" rx="0.9" fill="#0a0a0a" fillOpacity="0.65"/>
    </svg>
  );
}

/* ── DAME — Staunton queen: driedubbele kronenorbs, elegante rok ── */
function SvgDame() {
  return (
    <svg width="24" height="33" viewBox="0 0 28 37" fill="none" aria-hidden>
      <defs>
        <radialGradient id="qn-hd" cx="36%" cy="28%" r="72%">
          <stop offset="0%"   stopColor="#ececec"/>
          <stop offset="38%"  stopColor="#b4b4b4"/>
          <stop offset="75%"  stopColor="#585858"/>
          <stop offset="100%" stopColor="#181818"/>
        </radialGradient>
        <radialGradient id="qn-hs" cx="36%" cy="28%" r="72%">
          <stop offset="0%"   stopColor="#d8d8d8"/>
          <stop offset="50%"  stopColor="#909090"/>
          <stop offset="100%" stopColor="#282828"/>
        </radialGradient>
        <linearGradient id="qn-bd" x1="0" y1="0" x2="1" y2="1.2">
          <stop offset="0%"   stopColor="#d0d0d0"/>
          <stop offset="35%"  stopColor="#9c9c9c"/>
          <stop offset="72%"  stopColor="#505050"/>
          <stop offset="100%" stopColor="#181818"/>
        </linearGradient>
        <linearGradient id="qn-bs" x1="0" y1="0" x2="0.8" y2="1">
          <stop offset="0%"   stopColor="#bdbdbd"/>
          <stop offset="45%"  stopColor="#787878"/>
          <stop offset="100%" stopColor="#121212"/>
        </linearGradient>
        <radialGradient id="qn-sh" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#000" stopOpacity="0.35"/>
          <stop offset="100%" stopColor="#000" stopOpacity="0"/>
        </radialGradient>
      </defs>

      {/* Gronding-schaduw */}
      <ellipse cx="14" cy="14.5" rx="9.5" ry="1.5" fill="url(#qn-sh)"/>

      {/* === KROON — 3 orbs (links/midden/rechts) === */}
      {/* Linker orb */}
      <circle cx="6.5" cy="6.5" r="3.2" fill="url(#qn-hs)"/>
      <ellipse cx="5.4" cy="5.2" rx="1.3" ry="1.0" fill="#fff" fillOpacity="0.38"/>

      {/* Middelste orb (grootst) */}
      <circle cx="14" cy="4.5" r="4.2" fill="url(#qn-hd)"/>
      <ellipse cx="12.2" cy="3.0" rx="1.8" ry="1.4" fill="#fff" fillOpacity="0.44"/>
      <ellipse cx="13.8" cy="2.2" rx="0.7" ry="0.5" fill="#fff" fillOpacity="0.22"/>

      {/* Rechter orb */}
      <circle cx="21.5" cy="6.5" r="3.2" fill="url(#qn-hs)"/>
      <ellipse cx="20.4" cy="5.2" rx="1.3" ry="1.0" fill="#fff" fillOpacity="0.38"/>

      {/* === KROONBAND (verbindt de 3 orbs) === */}
      <path
        d="M 3.5,8.5
           Q 6.5,9.5  10,10.2
           Q 14,10.8  18,10.2
           Q 21.5,9.5  24.5,8.5
           L 23.5,13.5
           L 4.5,13.5
           Z"
        fill="url(#qn-bd)"
      />
      {/* Kroonband licht-rand linksboven */}
      <path
        d="M 3.5,8.5 Q 6.5,9.5 10,10.2 L 9.5,13.5 L 4.5,13.5 Z"
        fill="#fff" fillOpacity="0.10"
      />

      {/* === KRAAG === */}
      <ellipse cx="14" cy="13.8" rx="5.0" ry="2.0" fill="#000" fillOpacity="0.28"/>
      <ellipse cx="14" cy="13.5" rx="4.5" ry="1.8" fill="url(#qn-bd)"/>

      {/* === LICHAAM (bredere rok) === */}
      <path
        d="M 9.5,13.5
           C 8.0,15.0  5.5,19.0  4.5,27.0
           L 23.5,27.0
           C 22.5,19.0  20.0,15.0  18.5,13.5
           Z"
        fill="url(#qn-bd)"
      />
      {/* Licht strip links */}
      <path
        d="M 9.5,13.5
           C 8.8,15.0  8.0,18.0  7.2,22.5
           L 9.0,22.5
           C 9.8,18.5  10.6,16.0  11.2,14.8
           Z"
        fill="#fff" fillOpacity="0.10"
      />

      {/* === BASIS === */}
      <ellipse cx="14" cy="27.0" rx="10.2" ry="2.5" fill="url(#qn-bd)"/>
      <rect x="3.2" y="27.8" width="21.6" height="5.0" rx="2.5" fill="url(#qn-bs)"/>
      <ellipse cx="11.5" cy="28.5" rx="7.0" ry="1.1" fill="#fff" fillOpacity="0.12"/>
      <rect x="2.8" y="32.0" width="22.4" height="1.8" rx="0.9" fill="#090909" fillOpacity="0.65"/>
    </svg>
  );
}

/* ── KONING — Staunton king: latijns kruis, kroonband, brede body ── */
function SvgKoning() {
  return (
    <svg width="24" height="33" viewBox="0 0 28 37" fill="none" aria-hidden>
      <defs>
        <radialGradient id="kg-hd" cx="36%" cy="28%" r="72%">
          <stop offset="0%"   stopColor="#ededed"/>
          <stop offset="38%"  stopColor="#b0b0b0"/>
          <stop offset="75%"  stopColor="#545454"/>
          <stop offset="100%" stopColor="#181818"/>
        </radialGradient>
        <linearGradient id="kg-cr" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor="#dcdcdc"/>
          <stop offset="42%"  stopColor="#a0a0a0"/>
          <stop offset="100%" stopColor="#282828"/>
        </linearGradient>
        <linearGradient id="kg-bd" x1="0" y1="0" x2="1" y2="1.2">
          <stop offset="0%"   stopColor="#d0d0d0"/>
          <stop offset="35%"  stopColor="#9a9a9a"/>
          <stop offset="72%"  stopColor="#4c4c4c"/>
          <stop offset="100%" stopColor="#161616"/>
        </linearGradient>
        <linearGradient id="kg-bs" x1="0" y1="0" x2="0.8" y2="1">
          <stop offset="0%"   stopColor="#bbbbbb"/>
          <stop offset="45%"  stopColor="#747474"/>
          <stop offset="100%" stopColor="#101010"/>
        </linearGradient>
        <radialGradient id="kg-sh" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#000" stopOpacity="0.35"/>
          <stop offset="100%" stopColor="#000" stopOpacity="0"/>
        </radialGradient>
      </defs>

      {/* === LATIJNS KRUIS === */}
      {/* Verticale balk */}
      <rect x="12.0" y="1.0" width="4.0" height="12.5" rx="1.8" fill="url(#kg-cr)"/>
      {/* Horizontale balk */}
      <rect x="7.5"  y="4.2" width="13.0" height="3.8" rx="1.7" fill="url(#kg-cr)"/>
      {/* Kruis specular highlight (verticaal) */}
      <rect x="12.5" y="1.5" width="1.8" height="4.0" rx="0.8" fill="#fff" fillOpacity="0.35"/>
      {/* Kruis specular highlight (horizontaal) */}
      <rect x="8.2"  y="4.5" width="5.5" height="1.4" rx="0.6" fill="#fff" fillOpacity="0.28"/>

      {/* Gronding-schaduw */}
      <ellipse cx="14" cy="16.0" rx="5.5" ry="1.5" fill="url(#kg-sh)"/>

      {/* === KROONBOL (kleine bol onder het kruis) === */}
      <circle cx="14" cy="13.0" r="2.8" fill="url(#kg-hd)"/>
      <ellipse cx="12.8" cy="12.0" rx="1.2" ry="0.9" fill="#fff" fillOpacity="0.38"/>

      {/* === KROONBAND === */}
      <ellipse cx="14" cy="15.5" rx="5.5" ry="2.0" fill="#000" fillOpacity="0.28"/>
      <ellipse cx="14" cy="15.2" rx="4.8" ry="1.8" fill="url(#kg-bd)"/>

      {/* === LICHAAM === */}
      <path
        d="M 9.5,15.2
           C 8.0,16.5  5.8,20.0  5.0,27.0
           L 23.0,27.0
           C 22.2,20.0  20.0,16.5  18.5,15.2
           Z"
        fill="url(#kg-bd)"
      />
      {/* Licht strip links */}
      <path
        d="M 9.5,15.2
           C 8.8,16.5  8.0,19.5  7.4,23.0
           L 9.2,23.0
           C 9.8,19.8  10.6,17.2  11.2,15.8
           Z"
        fill="#fff" fillOpacity="0.10"
      />

      {/* === BASIS === */}
      <ellipse cx="14" cy="27.0" rx="9.5" ry="2.4" fill="url(#kg-bd)"/>
      <rect x="4.0" y="27.8" width="20.0" height="4.8" rx="2.4" fill="url(#kg-bs)"/>
      <ellipse cx="11.5" cy="28.5" rx="6.5" ry="1.1" fill="#fff" fillOpacity="0.12"/>
      <rect x="3.5" y="31.8" width="21.0" height="1.8" rx="0.9" fill="#0a0a0a" fillOpacity="0.65"/>
    </svg>
  );
}

/* ── TOREN — Staunton rook: 3 kantelen, rond lichaam, basis ─────── */
function SvgToren() {
  return (
    <svg width="24" height="33" viewBox="0 0 28 37" fill="none" aria-hidden>
      <defs>
        <linearGradient id="rk-hd" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor="#e0e0e0"/>
          <stop offset="38%"  stopColor="#a8a8a8"/>
          <stop offset="72%"  stopColor="#505050"/>
          <stop offset="100%" stopColor="#181818"/>
        </linearGradient>
        <linearGradient id="rk-bd" x1="0" y1="0" x2="1" y2="1.2">
          <stop offset="0%"   stopColor="#d0d0d0"/>
          <stop offset="35%"  stopColor="#9a9a9a"/>
          <stop offset="72%"  stopColor="#4e4e4e"/>
          <stop offset="100%" stopColor="#161616"/>
        </linearGradient>
        <linearGradient id="rk-bs" x1="0" y1="0" x2="0.8" y2="1">
          <stop offset="0%"   stopColor="#bebebe"/>
          <stop offset="45%"  stopColor="#767676"/>
          <stop offset="100%" stopColor="#111111"/>
        </linearGradient>
        <radialGradient id="rk-sh" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#000" stopOpacity="0.32"/>
          <stop offset="100%" stopColor="#000" stopOpacity="0"/>
        </radialGradient>
      </defs>

      {/* === DRIE KANTELEN (merlons) === */}
      {/* Linker kanteel */}
      <rect x="3.0" y="2.0" width="6.5" height="8.5" rx="1.2" fill="url(#rk-hd)"/>
      <rect x="3.8" y="2.5" width="3.0" height="2.5" rx="0.5" fill="#fff" fillOpacity="0.28"/>

      {/* Middelste kanteel (licht naar voren) */}
      <rect x="10.8" y="2.0" width="6.5" height="8.5" rx="1.2" fill="url(#rk-hd)"/>
      <rect x="11.6" y="2.5" width="3.2" height="2.8" rx="0.5" fill="#fff" fillOpacity="0.35"/>

      {/* Rechter kanteel */}
      <rect x="18.5" y="2.0" width="6.5" height="8.5" rx="1.2" fill="url(#rk-hd)"/>
      <rect x="19.2" y="2.5" width="3.0" height="2.5" rx="0.5" fill="#fff" fillOpacity="0.22"/>

      {/* Kantelen-verbindingsbalk (schietgaten) */}
      <rect x="3.0" y="10.5" width="22.0" height="3.8" fill="url(#rk-bd)"/>
      {/* Licht strip op verbindingsbalk */}
      <rect x="3.2" y="10.7" width="8.0" height="1.2" fill="#fff" fillOpacity="0.14"/>

      {/* Gronding-schaduw lichaam */}
      <ellipse cx="14" cy="17.0" rx="9.5" ry="1.5" fill="url(#rk-sh)"/>

      {/* === LICHAAM (rechthoekig, toren-karakteristiek) === */}
      {/* Bovenrand lichaam */}
      <ellipse cx="14" cy="14.5" rx="9.5" ry="2.2" fill="url(#rk-bd)"/>
      {/* Lichaam zijkanten */}
      <path
        d="M 5.0,14.5
           C 4.5,15.5  4.2,19.5  4.5,27.0
           L 23.5,27.0
           C 23.8,19.5  23.5,15.5  23.0,14.5
           Z"
        fill="url(#rk-bd)"
      />
      {/* Licht strip links lichaam */}
      <path
        d="M 5.0,14.5 C 4.8,15.5 4.6,19.0 4.8,23.0 L 6.5,23.0 C 6.3,19.0 6.3,16.0 6.2,15.0 Z"
        fill="#fff" fillOpacity="0.10"
      />

      {/* === BASIS === */}
      <ellipse cx="14" cy="27.0" rx="10.5" ry="2.5" fill="url(#rk-bd)"/>
      <rect x="3.0" y="27.8" width="22.0" height="4.8" rx="2.4" fill="url(#rk-bs)"/>
      <ellipse cx="11.5" cy="28.5" rx="7.0" ry="1.1" fill="#fff" fillOpacity="0.12"/>
      <rect x="2.5" y="31.8" width="23.0" height="1.8" rx="0.9" fill="#090909" fillOpacity="0.65"/>
    </svg>
  );
}

/* ── SVG piece wrapper met float-animatie ────────────────────────── */
function ChessPieceSvg({ children, animClass, label }: { children: React.ReactNode; animClass: string; label: string }) {
  return (
    <div className={`nav-svg-piece ${animClass}`} aria-label={label} role="img">
      {children}
    </div>
  );
}

const PRIMARY_TABS = [
  {
    id: 'dashboard',
    label: 'HQ',
    icon: <IconHome />,
    isHome: true,
  },
  {
    id: 'opening',
    label: 'OPENING',
    icon: <ChessPieceSvg animClass="nav-piece-a" label="pion — Opening"><SvgPion /></ChessPieceSvg>,
    isHome: false,
  },
  {
    id: 'positie',
    label: 'POSITIE',
    icon: <ChessPieceSvg animClass="nav-piece-b" label="dame — Positie"><SvgDame /></ChessPieceSvg>,
    isHome: false,
  },
  {
    id: 'eindspel',
    label: 'EINDSPEL',
    icon: <ChessPieceSvg animClass="nav-piece-c" label="koning — Eindspel"><SvgKoning /></ChessPieceSvg>,
    isHome: false,
  },
  {
    id: 'info',
    label: 'INFO',
    icon: <ChessPieceSvg animClass="nav-piece-d" label="toren — Info"><SvgToren /></ChessPieceSvg>,
    isHome: false,
  },
];

export default function NavSidebar({ currentPage, onNavigate }: Props) {
  return (
    <nav className="nav-bar">
      <div className="nav-bar-inner">
        {PRIMARY_TABS.map((tab) => {
          const active = currentPage === tab.id;
          return (
            <button
              key={tab.id}
              className={`nav-tab${active ? ' nav-tab-active' : ''}${tab.isHome ? ' nav-tab-home' : ' nav-tab-chess'}`}
              onClick={() => onNavigate(tab.id)}
              aria-label={tab.label}
            >
              <span className={`nav-tab-icon${active ? ' nav-tab-icon-active' : ''}`}>
                {tab.icon}
              </span>
              <span className={`nav-tab-label-v4${active ? ' nav-tab-label-active' : ''}`}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
